import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { formatCurrency, formatDate } from '../lib/utils';
import { apiHelpers } from '../lib/api';
import type { Document, Payment } from '../lib/types';
import type { HomeDashboardResponse } from '../lib/accessRequests';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../hooks/usePermissions';
import { DASHBOARD_FETCH_PERMISSIONS } from '../lib/permissions';
import { useAuthStore } from '../stores/authStore';
import {
  FileText,
  Building2,
  CreditCard,
  AlertTriangle,
  Calendar,
  Bell,
} from 'lucide-react';
import { StatCard } from '../components/ui/StatCard';
import { cn } from '../lib/utils';
import { HomeModuleCard } from '../components/access/HomeModuleCard';

// Activity item component
function ActivityItem({
  icon: Icon,
  title,
  description,
  time,
  color,
  className,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  time: string;
  color: string;
  className?: string;
}) {
  return (
    <div className={cn("flex items-start gap-4 py-3", className)}>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {title}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {description}
        </p>
      </div>
      <span className="text-xs text-gray-400">{time}</span>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { hasAnyPermission } = usePermissions();

  const canFetchDocuments = hasAnyPermission([...DASHBOARD_FETCH_PERMISSIONS.documents]);
  const canFetchPayments = hasAnyPermission([...DASHBOARD_FETCH_PERMISSIONS.payments]);
  const canFetchContracts = hasAnyPermission([...DASHBOARD_FETCH_PERMISSIONS.contracts]);
  const canFetchBudget = hasAnyPermission([...DASHBOARD_FETCH_PERMISSIONS.budget]);

  const statsQueryEnabled =
    canFetchDocuments || canFetchPayments || canFetchContracts || canFetchBudget;

  const { data: homeDashboard, isLoading: homeLoading } = useQuery({
    queryKey: ['home-dashboard'],
    queryFn: async () => {
      try {
        const res = await apiHelpers.users.homeDashboard();
        return res.data as HomeDashboardResponse;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return { cards: [] } satisfies HomeDashboardResponse;
        }
        throw error;
      }
    },
    retry: false,
  });

  // Fetch dashboard data (legacy widgets — only when user has grants)
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: [
      'dashboard-stats',
      canFetchDocuments,
      canFetchPayments,
      canFetchContracts,
      canFetchBudget,
    ],
    enabled: statsQueryEnabled,
    queryFn: async () => {
      const [docsRes, pendingRes, overdueRes, contractsRes, budgetRes] = await Promise.allSettled([
        canFetchDocuments
          ? apiHelpers.documents.list({ page_size: 1 })
          : Promise.resolve({ data: { count: 0 } }),
        canFetchPayments
          ? apiHelpers.payments.getAll({ status: 'pending', page_size: 1 })
          : Promise.resolve({ data: { count: 0 } }),
        canFetchPayments
          ? apiHelpers.payments.getAll({ status: 'overdue', page_size: 1 })
          : Promise.resolve({ data: { count: 0 } }),
        canFetchContracts
          ? apiHelpers.contracts.list({ status: 'active', page_size: 1 })
          : Promise.resolve({ data: { count: 0 } }),
        canFetchBudget
          ? apiHelpers.budget.summary()
          : Promise.resolve({ data: {} }),
      ]);

      const docs   = docsRes.status      === 'fulfilled' ? docsRes.value.data      : null;
      const pending  = pendingRes.status  === 'fulfilled' ? pendingRes.value.data  : null;
      const overdue  = overdueRes.status  === 'fulfilled' ? overdueRes.value.data  : null;
      const contracts = contractsRes.status === 'fulfilled' ? contractsRes.value.data : null;
      const budget   = budgetRes.status   === 'fulfilled' ? budgetRes.value.data   : null;

      return {
        totalDocuments:  docs?.count      ?? 0,
        activeContracts: contracts?.count ?? 0,
        pendingPayments: pending?.count   ?? 0,
        overduePayments: overdue?.count   ?? 0,
        monthlyIncome:   budget?.total_income   ?? budget?.income   ?? 0,
        monthlyExpenses: budget?.total_expenses ?? budget?.expenses ?? 0,
      };
    },
    retry: false,
  });

  const recentActivityEnabled = canFetchDocuments || canFetchPayments;

  const { data: recentActivity } = useQuery({
    queryKey: ['recent-activity', canFetchDocuments, canFetchPayments],
    enabled: recentActivityEnabled,
    queryFn: async () => {
      const activity: Array<{
        id: string;
        type: 'document' | 'payment';
        title: string;
        description: string;
        time: string;
      }> = [];

      const [docsRes, paymentsRes] = await Promise.allSettled([
        canFetchDocuments
          ? apiHelpers.documents.list({ page_size: 3, ordering: '-created_at' })
          : Promise.resolve({ data: { results: [] } }),
        canFetchPayments
          ? apiHelpers.payments.getAll({ page_size: 3, ordering: '-created_at' })
          : Promise.resolve({ data: { results: [] } }),
      ]);

      if (docsRes.status === 'fulfilled') {
        const results: Document[] = docsRes.value.data?.results ?? [];
        results.forEach((d: Document) => {
          activity.push({
            id: `doc-${d.id}`,
            type: 'document',
            title: d.title || d.original_filename || d.name || '-',
            description: d.category?.name || '',
            time: d.created_at || d.upload_date || '',
          });
        });
      }

      if (paymentsRes.status === 'fulfilled') {
        const results: Payment[] = paymentsRes.value.data?.results ?? [];
        results.forEach((p: Payment) => {
          activity.push({
            id: `pay-${p.id}`,
            type: 'payment',
            title: p.description || formatCurrency(Number(p.amount), p.currency ?? 'RON'),
            description: p.tenant?.name || p.tenant_name || p.payer_name || p.apartment_name || '',
            time: p.paid_date || p.created_at || p.due_date || '',
          });
        });
      }

      return activity;
    },
    retry: false,
  });

  const { data: upcomingPayments } = useQuery({
    queryKey: ['upcoming-payments', canFetchPayments],
    enabled: canFetchPayments,
    queryFn: async () => {
      try {
        const [pendingRes, overdueRes] = await Promise.allSettled([
          apiHelpers.payments.getAll({
            status: 'pending',
            ordering: 'due_date',
            page_size: 5,
          }),
          apiHelpers.payments.getAll({
            status: 'overdue',
            ordering: 'due_date',
            page_size: 5,
          }),
        ]);
        const pendingResults: Payment[] = pendingRes.status === 'fulfilled' ? pendingRes.value.data?.results ?? [] : [];
        const overdueResults: Payment[] = overdueRes.status === 'fulfilled' ? overdueRes.value.data?.results ?? [] : [];
        const combined = [...overdueResults, ...pendingResults].slice(0, 5);
        return combined.map((p: Payment) => ({
          id: p.id,
          tenant:    p.tenant?.name    || p.tenant_name  || p.payer_name || '-',
          apartment: p.apartment?.name || p.apartment_name || p.apartment_number || p.description || '-',
          amount:    Number(p.amount)  || 0,
          dueDate:   p.due_date        || p.dueDate       || '',
          isOverdue: p.status === 'overdue' || (p.due_date && new Date(p.due_date) < new Date()),
        }));
      } catch {
        return [];
      }
    },
    retry: false,
  });

  const showLegacyStatsLoading = statsQueryEnabled && statsLoading;
  const homeCards = homeDashboard?.cards ?? [];

  if (homeLoading || showLegacyStatsLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="card p-6 h-36 skeleton" />
          ))}
        </div>
      </div>
    );
  }

  const showLegacyStatGrid =
    canFetchDocuments || canFetchContracts || (canFetchPayments && homeCards.length === 0);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {user?.first_name
            ? t('dashboard.greetingName', { name: user.first_name })
            : t('dashboard.greeting')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* DEC-003 module cards (apartments + payments) */}
      {homeCards.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {homeCards.map((card) => (
            <HomeModuleCard key={card.module} card={card} />
          ))}
        </div>
      ) : null}

      {/* Legacy stats — only when user has module grants (older backends without home-dashboard) */}
      {showLegacyStatGrid && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {canFetchDocuments && (
            <StatCard
              title={t('dashboard.totalDocuments')}
              value={stats?.totalDocuments ?? 0}
              icon={FileText}
              color="primary"
            />
          )}
          {canFetchContracts && (
            <StatCard
              title={t('dashboard.activeContracts')}
              value={stats?.activeContracts ?? 0}
              icon={Building2}
              color="success"
            />
          )}
          {canFetchPayments && homeCards.length === 0 && (
            <>
              <StatCard
                title={t('dashboard.pendingPayments')}
                value={stats?.pendingPayments ?? 0}
                icon={CreditCard}
                color="warning"
              />
              <StatCard
                title={t('dashboard.overduePayments')}
                value={stats?.overduePayments ?? 0}
                icon={AlertTriangle}
                color="danger"
              />
            </>
          )}
        </div>
      )}

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent activity */}
        {recentActivityEnabled && (
        <div className="card">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('dashboard.recentActivity')}
            </h2>
          </div>
          <div className="p-4">
            {!recentActivity || recentActivity.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">
                  {t('dashboard.noRecentActivity')}
                </p>
              </div>
            ) : (
              recentActivity.map((activity, index) => (
                <ActivityItem
                  key={activity.id}
                  className={
                    index > 0
                      ? 'border-t border-gray-100 dark:border-gray-700'
                      : undefined
                  }
                  icon={activity.type === 'document' ? FileText : CreditCard}
                  title={activity.title}
                  description={activity.description}
                  time={activity.time ? formatDate(activity.time) : ''}
                  color={
                    activity.type === 'document'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-green-100 text-green-600'
                  }
                />
              ))
            )}
          </div>
        </div>
        )}

        {/* Upcoming payments */}
        {canFetchPayments && (
        <div className="card">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('dashboard.upcomingPayments')}
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {!upcomingPayments || upcomingPayments.length === 0 ? (
              <div className="py-10 text-center">
                <CreditCard className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">
                  {t('dashboard.noUpcomingPayments')}
                </p>
              </div>
            ) : (
              upcomingPayments.map((payment) => (
                <div
                  key={payment.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    payment.isOverdue
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-gray-50 dark:bg-gray-700/50'
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {payment.tenant}
                      </p>
                      {payment.isOverdue && (
                        <span className="text-xs font-medium text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 rounded-sm">
                          {t('dashboard.overdue', 'Overdue')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {payment.apartment}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(payment.amount)}
                    </p>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="w-3 h-3" />
                      {payment.dueDate ? formatDate(payment.dueDate) : '-'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        )}
      </div>

      {/* Budget overview */}
      {canFetchBudget && (
      <div className="card">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('dashboard.currentBudget')}
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('dashboard.income')}
              </p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {formatCurrency(stats?.monthlyIncome || 0)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('dashboard.expenses')}
              </p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {formatCurrency(stats?.monthlyExpenses || 0)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('dashboard.balance')}
              </p>
              <p className="text-2xl font-bold text-primary-600 mt-1">
                {formatCurrency(
                  (stats?.monthlyIncome || 0) - (stats?.monthlyExpenses || 0)
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
